// Cue 庫的 cue「最近刪除」（後台 cf-worker/cueLibraryTrash.mjs，migration 0059）：
//   刪 cue 不跳確認，直接刪；提示上有「復原」；刪掉的留 30 天，這段時間可以從「最近刪除」救回來。
// 這個檔案：畫面上要寫的字 + 刪 / 復原的流程（純函式 + 小流程，libraryCueTrash.test.ts 有測）。
// 跟「最近刪除的歌」（songTrashApi.ts）同一套說法。

import { daysLeftText, isSoon } from './songTrashApi';
import { formatDateTime } from './serverTime';
import type { DeletedLibraryCue, RestoredLibraryCue } from './cueLibraryApi';
import { toast } from './toast';

export const LIBRARY_CUE_KEEP_DAYS = 30;
export { daysLeftText, isSoon };

/** 刪完的提示（有「復原」按鈕）：「已刪除 cue 12」 */
export function deletedCueToast(label: string): string {
  return `已刪除 cue ${label}`;
}

/** 提示上的按鈕字 */
export const UNDO_LABEL = '復原';

/** 復原 / 救回來成功的提示；原編號被占走了要說明新編號 */
export function restoredCueToast(r: Pick<RestoredLibraryCue, 'label' | 'relabeledFrom'>): string {
  return r.relabeledFrom
    ? `已救回 cue，原本的編號 ${r.relabeledFrom} 已經被別的 cue 用了，改成 ${r.label}`
    : `已救回 cue ${r.label}`;
}

/** 救回來失敗：後台寫好了白話（410 超過 30 天、409 剛剛被救回來 / 清掉了），直接用 */
export function restoreCueErrorText(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  return '救不回來：' + m;
}

/** 「3 個物件、2 個機關」（沒有的不寫；都沒有 = 空字串） */
export function deletedCueInside(c: Pick<DeletedLibraryCue, 'counts'>): string {
  return [
    c.counts.objects > 0 ? `${c.counts.objects} 個物件` : '',
    c.counts.mechanisms > 0 ? `${c.counts.mechanisms} 個機關` : '',
  ].filter(Boolean).join('、');
}

/** 「王導 刪的 · 11/02 14:30」 */
export function deletedCueMeta(c: Pick<DeletedLibraryCue, 'deletedBy' | 'deletedAt'>): string {
  const who = c.deletedBy?.name ? `${c.deletedBy.name} 刪的` : '刪掉的';
  return [who, formatDateTime(c.deletedAt)].filter(Boolean).join(' · ');
}

/** 入口按鈕：「最近刪除（2）」 */
export function cueTrashEntryLabel(count: number): string {
  return `最近刪除（${count}）`;
}

export interface UndoableDelete {
  /** 真的去刪（成功才會跳「復原」提示；失敗請丟出錯誤，這裡不接） */
  remove: () => Promise<unknown>;
  /** 復原 */
  restore: () => Promise<RestoredLibraryCue>;
  label: string;
  /** 刪完 / 復原完之後重新讀清單；restored = 剛救回來的那個 cue（刪完呼叫時沒有） */
  after?: (restored?: RestoredLibraryCue) => void | Promise<unknown>;
}

/**
 * 刪 → 提示「已刪除 cue X，復原」。按復原馬上救回來；救不回來（超過期限、被清掉）說明原因。
 * 回傳 true = 刪掉了。
 */
export async function removeWithUndo(o: UndoableDelete): Promise<boolean> {
  await o.remove();
  toast.success(deletedCueToast(o.label), 10000, {
    label: UNDO_LABEL,
    onClick: () => {
      void o.restore().then(
        (r) => { toast.success(restoredCueToast(r)); void o.after?.(r); },
        (e: unknown) => { toast.error(restoreCueErrorText(e)); void o.after?.(); },
      );
    },
  });
  void o.after?.();
  return true;
}
